import React, {useEffect, useRef, useState} from 'react';
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import {subMonths} from "date-fns";
import Content from "./components/content.jsx";
import Filters from "./components/filters.jsx";

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 3),
    dtEnd: new Date(),
    module: null,
    form: null,
    fields: []
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    module: "Módulo",
    form: "Formulário",
    fields: "Campos do relatório",
};

const API_URL = import.meta.env.VITE_API_URL;

const GenericFormAnalyse = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);
    const [appliedForm, setAppliedForm] = useState(null);

    const [title] = useState("Análise de formulários");
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [fieldsModalOpen, setFieldsModalOpen] = useState(false);

    const auditableFilters = (activeFilters) => ({
        ...activeFilters,
        moduleLabel: undefined,
        module: activeFilters.moduleLabel,
        dtStart: activeFilters.dtStart ? ReportUtils.formatDateTime(activeFilters.dtStart) : "",
        dtEnd: activeFilters.dtEnd ? ReportUtils.formatDateTime(activeFilters.dtEnd) : "",
    });

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(ReportUtils.filters(auditableFilters(filters), filterLabels));

    }, [reportData]);

    const runReport = async (activeFilters) => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null);
                setAppliedForm(null);
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(activeFilters.dtStart, activeFilters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            if (!ReportUtils.requiredFilterValidate(activeFilters.module, "Módulo", setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(activeFilters.form?.value, "Formulário", setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(activeFilters.fields, "Campos do relatório", setLoading)) return;

            const formattedFilters = {
                dtStart: activeFilters.dtStart ? ReportUtils.formatDateTime(activeFilters.dtStart) : null,
                dtEnd: activeFilters.dtEnd ? ReportUtils.formatDateTime(activeFilters.dtEnd) : null,
            };

            const auditFilters = ReportUtils.filters(auditableFilters(activeFilters), filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/generic_form_analyse/apply", {
                ...formattedFilters,
                form: activeFilters.form?.value,
                fields: activeFilters.fields ?? [],
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedForm(activeFilters.form ?? null);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.log("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    const apply = () => runReport(filters);

    const applyWithFields = (fields) => {
        setFilters(prev => ({...prev, fields}));
        runReport({...filters, fields});
    };

    useGlobalKey("Enter", apply, !fieldsModalOpen);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
        setAppliedForm(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    const total = reportData?.total ?? 0;

    return (
        <>
            <div className={"flex"}>
                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={total}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-0' : ''}`}>
                        <Menu
                            buttonRef={buttonRef}
                            toggleFilters={toggleFilters}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            apply={apply}
                            clearSearch={clearSearch}
                        >
                            <Filters
                                setFilters={setFilters}
                                filters={filters}
                                modalOpen={fieldsModalOpen}
                                setModalOpen={setFieldsModalOpen}
                                onApply={applyWithFields}
                            />
                            <AutoDatetime setFilters={setFilters}/>
                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || total === 0 ? 'mt-14.75' : 'mt-15.25' } print:-mt-3.75! transform ${!reportData || total === 0 ? 'bg-[#f3f3f4]! border-none! -translate-y-1.25!' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || total === 0 ? 'p-0' : 'px-5 py-1.25'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={total}/>
                            <Content
                                reportData={reportData}
                                data={total}
                                entity={headerSelected}
                                title={title}
                                structure={appliedForm?.structure}
                                templateId={appliedForm?.value}
                                collapsed={collapsed}
                                setLoading={setLoading}
                                printComponentRef={printComponentRef}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </>
    );
};

export default GenericFormAnalyse;